import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
	EncumbranceActionLabels,
	EncumbranceTypeLabels,
} from '@core/util/encumbrance';
import { createId } from '@core/util/file';
import { DATE_FORMAT } from '@models/businesses';
import {
	Encumbrance,
	EncumbranceActionType,
	EncumbranceType,
	MortgagePriority,
} from '@models/encumbrance';
import { MatterFileType } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { AuthState } from '@state/auth';
import { AuthUser } from '@state/auth/auth.models';
import {
	EncumbrancesActions,
	EncumbrancesDispatch,
	MatterEncumbrancesState,
} from '@state/matter/encumbrances';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { format, isValid, parse } from 'date-fns';

import { Observable } from 'rxjs';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { RadioGroupItem } from 'src/app/shared/atomic/atoms/radio-group/radio-group.component';

export interface TeranetCreateEncumbranceData {
	instrumentType: string;
	registrationNumber: string;
	registrationDate: string;
}

const EncumbranceOptions: DropdownOption<EncumbranceType>[] = [
	{ name: 'Mortgage', value: EncumbranceType.MORTGAGE },
	{ name: 'Lien', value: EncumbranceType.LIEN },
	{
		name: 'Notice of Security',
		value: EncumbranceType.NOTICE_OF_SECURITY_INTEREST,
	},
];

const EncumbranceActions: RadioGroupItem<EncumbranceActionType>[] = [
	{
		value: EncumbranceActionType.DISCHARGE,
		label: EncumbranceActionLabels[EncumbranceActionType.DISCHARGE],
	},
	{
		value: EncumbranceActionType.REMAIN,
		label: EncumbranceActionLabels[EncumbranceActionType.REMAIN],
	},
];

@UntilDestroy()
@Component({
	selector: 'app-teranet-create-encumbrance',
	templateUrl: './teranet-create-encumbrance.component.html',
	standalone: false,
})
export class TeranetCreateEncumbranceComponent implements OnInit {
	@Select(MatterEncumbrancesState.getSorted) allEncumbrances: Observable<
		Encumbrance[]
	>;
	@SelectSnapshot(AuthState.getLoggedInUser) loggedInUser: AuthUser;
	@ViewSelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;

	public priority = 1;
	public instrumentType: string;
	public encumbranceTypeLabel = EncumbranceTypeLabels[EncumbranceType.MORTGAGE];
	public encumbranceType = EncumbranceType.MORTGAGE;
	public priorityOptions = MortgagePriority;
	public selectedEncumbranceAction = EncumbranceActionType.DISCHARGE;
	public encumbranceActionLabel =
		EncumbranceActionLabels[EncumbranceActionType.DISCHARGE];
	public encumbranceActions = EncumbrancesActions;
	public registrationNumber: string;
	public registrationDate: string;
	public amount: string;
	public encumbranceOptions = EncumbranceOptions;
	public encumbranceActionsOptions = EncumbranceActions;
	public fileTypes = MatterFileType;
	public undertaking = true; // true = include encumbrance in undertaking to discharge (default); not user-selectable atm

	constructor(
		public dialogRef: MatDialogRef<TeranetCreateEncumbranceComponent>,
		@Inject(MAT_DIALOG_DATA) public data: TeranetCreateEncumbranceComponent,
		private encumbranceDispatch: EncumbrancesDispatch,
	) {}

	ngOnInit(): void {
		this.instrumentType = this.data.instrumentType;
		this.registrationNumber = this.data.registrationNumber;

		if (this.data.registrationDate?.length > 0) {
			const _date = parse(this.data.registrationDate, 'yyyy/MM/dd', new Date());
			if (_date && isValid(_date)) {
				this.registrationDate = format(_date, DATE_FORMAT);
			}
		}

		if (this.data.instrumentType === 'LIEN') {
			this.encumbranceType = EncumbranceType.LIEN;
			this.encumbranceTypeLabel = EncumbranceTypeLabels[EncumbranceType.LIEN];
		}

		if (this.data.instrumentType === 'NOTICE') {
			this.encumbranceType = EncumbranceType.NOTICE_OF_SECURITY_INTEREST;
			this.encumbranceTypeLabel =
				EncumbranceTypeLabels[EncumbranceType.NOTICE_OF_SECURITY_INTEREST];
		}

		this.allEncumbrances
			.pipe(untilDestroyed(this))
			.subscribe((encumbrances) => {
				if (encumbrances) {
					const mortgageEncumbrances = encumbrances.filter(
						(x) =>
							x.type === EncumbranceType.MORTGAGE &&
							(x.action === EncumbranceActionType.DISCHARGE ||
								x.action === EncumbranceActionType.REMAIN),
					);
					this.priority = mortgageEncumbrances?.length + 1;
				}
			});
	}

	save() {
		const id = createId();

		this.encumbranceDispatch.CreateFromTeranet(
			id,
			this.encumbranceType,
			this.selectedEncumbranceAction,
			this.registrationNumber,
			this.registrationDate,
			this.priority,
			this.undertaking,
		);

		this.dialogRef.close();
	}

	close() {
		this.dialogRef.close();
	}

	selectEncumbranceType(
		$event: DropdownResult<DropdownOption<EncumbranceType>>,
	) {
		this.encumbranceTypeLabel = EncumbranceTypeLabels[$event.result.value];
		this.encumbranceType = $event.result.value;
	}
}
